import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List, useDataGrid } from "@refinedev/mui";

// One row of the `story_views` table (GET /api/v2/admin/story-views), joined to
// whatever still names the story. `id` is the story's hex shelf id (e.g.
// "0925d52c") — the table is keyed by story; only Story Studio stories are listed. `title` / `authorId` / `authorName` are null
// for a story that was deleted — the counters outlive it.
type StoryViewRecord = {
  id: string;
  startCount: number;
  finishCount: number;
  finishRate: number | null;
  title: string | null;
  authorId: number | null;
  authorName: string | null;
  inLibrary: boolean;
};

const formatCount = (value: number) => value.toLocaleString();

export const StoryViewList = () => {
  // Server-ordered: most-started first. Columns are not sortable because the
  // shared data provider sends page/limit only — client-side sorting would
  // reorder the current page and read as if it had sorted the whole table.
  const { dataGridProps } = useDataGrid({});

  const columns = React.useMemo<GridColDef<StoryViewRecord>[]>(
    () => [
      {
        field: "id",
        headerName: "Story",
        minWidth: 110,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <code>{row.id}</code>,
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.title ? (
            <Tooltip title={row.title}>
              <Typography variant="body2" noWrap>
                {row.title}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              (story deleted)
            </Typography>
          ),
      },
      {
        field: "authorName",
        headerName: "Author",
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => {
          if (row.authorId == null) {
            return <span style={{ opacity: 0.6 }}>-</span>;
          }
          return (
            <span>
              {row.authorName ? `${row.authorId} (${row.authorName})` : row.authorId}
            </span>
          );
        },
      },
      {
        field: "inLibrary",
        headerName: "Published",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.inLibrary ? (
            <Chip size="small" label="Library" color="success" variant="outlined" />
          ) : (
            <Chip size="small" label="Draft" variant="outlined" />
          ),
      },
      {
        field: "startCount",
        headerName: "Starts",
        minWidth: 110,
        align: "right",
        headerAlign: "right",
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <span>{formatCount(row.startCount)}</span>,
      },
      {
        field: "finishCount",
        headerName: "Finishes",
        minWidth: 110,
        align: "right",
        headerAlign: "right",
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <span>{formatCount(row.finishCount)}</span>,
      },
      {
        field: "finishRate",
        headerName: "Finish Rate",
        minWidth: 180,
        sortable: false,
        display: "flex",
        // Share of starts that reached the end — the number these counters
        // exist for. Null (no starts yet) shows a dash, not 0%.
        renderCell: ({ row }) => {
          if (row.finishRate == null) {
            return <span style={{ opacity: 0.6 }}>-</span>;
          }
          const percent = Math.round(row.finishRate * 100);
          return (
            <Stack sx={{ width: "100%" }} spacing={0.5}>
              <Typography variant="body2">{percent}%</Typography>
              <LinearProgress
                variant="determinate"
                // Finishes can exceed starts on rows that predate start
                // tracking, so clamp rather than overflow the bar.
                value={Math.min(percent, 100)}
                color={percent >= 50 ? "success" : percent >= 20 ? "warning" : "error"}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Stack>
          );
        },
      },
    ],
    []
  );

  return (
    <List canCreate={false}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Aggregate play counters per story (no per-user tracking), most-started
          first. Counters survive the story they belong to.
        </Typography>
      </Box>
      <DataGrid {...dataGridProps} columns={columns} disableColumnFilter />
    </List>
  );
};
